/**
 * ScreenshotTool Component - WESTEER Design System
 *
 * @version 1.0
 * @updated 2026-02-16
 * @module core/map-tools/screenshot
 *
 * @description
 * Herramienta para capturar pantalla del mapa.
 * Permite exportar la vista actual como imagen PNG.
 *
 * @remarks
 * **Características**:
 * - Captura del canvas del mapa
 * - Descarga automática como PNG
 * - Opción de incluir controles UI
 * - Nombre de archivo con timestamp
 * - Feedback visual al capturar
 * - Modal de confirmación con preview
 *
 * @example
 * ```tsx
 * import { ScreenshotTool } from '@/core/map-tools/screenshot';
 *
 * function MapTools() {
 *   return (
 *     <ScreenshotTool
 *       map={mapInstance}
 *       onCapture={() => console.log('Capturada')}
 *     />
 *   );
 * }
 * ```
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { Camera, X, Download, Check } from 'lucide-react';
import './screenshot.css';

/**
 * Props para ScreenshotTool
 */
interface ScreenshotToolProps {
  /**
   * Instancia del mapa MapLibre
   */
  map: MapLibreMap | null;

  /**
   * Callback cuando se captura exitosamente
   */
  onCapture?: () => void;

  /**
   * Si el botón debe estar deshabilitado
   */
  disabled?: boolean;
}

/**
 * ScreenshotTool - Herramienta de captura de pantalla
 */
export function ScreenshotTool({ map, onCapture, disabled = false }: ScreenshotToolProps): JSX.Element {
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [capturing, setCapturing] = useState<boolean>(false);

  /**
   * Captura la pantalla del mapa
   */
  const captureScreenshot = (): void => {
    if (!map) {
      console.error('[ScreenshotTool] Mapa no disponible');
      return;
    }

    setCapturing(true);

    // Esperar a que el mapa termine de renderizar
    map.once('idle', () => {
      try {
        // Obtener el canvas del mapa
        const canvas = map.getCanvas();

        // Convertir canvas a blob
        canvas.toBlob((blob) => {
          if (!blob) {
            console.error('[ScreenshotTool] Error al crear blob');
            setCapturing(false);
            return;
          }

          // Crear URL del preview
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
          setShowPreview(true);
          setCapturing(false);

          onCapture?.();
        }, 'image/png');
      } catch (error) {
        console.error('[ScreenshotTool] Error al capturar:', error);
        setCapturing(false);
      }
    });

    // Forzar render del mapa
    map.triggerRepaint();
  };

  /**
   * Descarga la captura
   */
  const downloadScreenshot = (): void => {
    if (!previewUrl) return;

    // Crear nombre de archivo con timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `westeer-map-${timestamp}.png`;

    // Crear link de descarga
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cerrar preview
    handleClosePreview();
  };

  /**
   * Cierra el preview y limpia recursos
   */
  const handleClosePreview = (): void => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
    setShowPreview(false);
  };

  return (
    <>
      {/* Botón de captura (se renderiza en BottomToolbar) */}
      <button
        className="toolbar-square-btn"
        aria-label="Capturar pantalla"
        title="Capturar pantalla del mapa"
        onClick={captureScreenshot}
        disabled={disabled || capturing}
      >
        {capturing ? <Check size={20} className="screenshot-capturing" /> : <Camera size={20} />}
      </button>

      {/* Modal de preview */}
      {showPreview &&
        createPortal(
          <div className="screenshot-modal-overlay" onClick={handleClosePreview}>
            <div className="screenshot-modal" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="screenshot-modal-header">
                <h3 className="screenshot-modal-title">Captura de Pantalla</h3>
                <button
                  className="screenshot-modal-close"
                  onClick={handleClosePreview}
                  aria-label="Cerrar"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Preview de la imagen */}
              <div className="screenshot-modal-content">
                <div className="screenshot-preview-wrapper">
                  <img
                    src={previewUrl}
                    alt="Preview de captura"
                    className="screenshot-preview-image"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="screenshot-modal-actions">
                <button
                  className="screenshot-modal-btn screenshot-modal-btn-primary"
                  onClick={downloadScreenshot}
                >
                  <Download size={16} />
                  <span>Descargar PNG</span>
                </button>
                <button
                  className="screenshot-modal-btn screenshot-modal-btn-secondary"
                  onClick={handleClosePreview}
                >
                  Cerrar
                </button>
              </div>

              {/* Footer con info */}
              <div className="screenshot-modal-footer">
                <p className="screenshot-modal-info">
                  💡 La imagen se descargará con la fecha y hora actual
                </p>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
